import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import { sendTrackingInfo } from '@panel/commons';
import QualityCard from './QualityCard';
import { useTranslation } from 'react-i18next';
import { isPortalWithEcommerce } from '@panel/commons/helpers';
import Carousel from 'react-multi-carousel';
import ArrowDisabled from '@panel/commons/assets/icons/carousel-arrow-dis.svg';
import Arrow from '@panel/commons/assets/icons/carousel-arrow.svg';
import TipCard from './TipCard';
import PriceCard from './PriceCard';
import 'react-multi-carousel/lib/styles.css';

const responsive = {
  desktop: {
    breakpoint: {
      max: 3000,
      min: 990
    },
    items: 2,
    partialVisibilityGutter: 100,
    slidesToSlide: 2
  },
  tablet: {
    breakpoint: {
      max: 989,
      min: 464
    },
    items: 1,
    partialVisibilityGutter: 300
  },
  mobile: {
    breakpoint: {
      max: 464,
      min: 0
    },
    items: 1,
    partialVisibilityGutter: 30
  }
};

const TipCarousel = ({
  tips,
  redirectToEdit,
  device,
  setShowModal,
  postingQuality,
  isParticular,
  postingId,
  isIntegrator,
  publicationPlan,
  isDuplicate,
  isPenalized,
  isRentedOrSold,
  isAssistant
}) => {
  const { t } = useTranslation();
  const [filteredTips, setFilteredTips] = useState([]);
  const percentageCorrectness = postingQuality && postingQuality.percentage_correctness;
  // posibles valores de qualityStatus: ok, warn, error, not_apply
  const qualityStatus = postingQuality && postingQuality.quality_status;
  const qualityNotApply = qualityStatus === 'not_apply';
  const linkUpgradePlan = `/panel/publicador/planes/seleccion?postingId=${postingId}`;
  const isPostingWithoutQuality = isDuplicate || isPenalized || isRentedOrSold;
  const messageWithoutQuality =
    postingQuality &&
    postingQuality.validations &&
    postingQuality.validations[0] &&
    postingQuality.validations[0].message;

  // esta variable se crea ya que los usuarios particulares en LLVE no tienen disponible el ecommerce
  const isPortalWithoutEcommerce = isParticular && !isPortalWithEcommerce;

  const getQualityText = () => {
    if (isDuplicate) return t('panel.avisos.posting.quality.duplicated');
    if (isPenalized) return t('panel.avisos.posting.quality.penalized');
    if (percentageCorrectness >= 0 && !qualityNotApply) return t('panel.avisos.posting.quality.ok');
    return t('panel.avisos.posting.quality.withoutData');
  };

  useEffect(() => {
    // se filtran tips para particulares de LLVE que no se muestre el consejo de mejorar exposicion
    if (isPortalWithoutEcommerce) {
      const filteredList = tips.filter(tip => tip.tipId !== 'posting_exposure');
      setFilteredTips(filteredList);
    } else {
      setFilteredTips(tips);
    }
  }, [tips]);

  const openReportModal = () => {
    setShowModal(prev => {
      return {
        ...prev,
        reportsModal: true
      };
    });
  };

  const openTipsModal = () => {
    setShowModal(prev => {
      return {
        ...prev,
        performanceTips: true
      };
    });
  };

  const openUpgradeModal = () => {
    setShowModal(prev => {
      return {
        ...prev,
        upgrade: true
      };
    });
  };

  const tipsActionHandlers = {
    posting_main_picture: () => {
      sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Consejos-Principal');
      if (isIntegrator) {
        setShowModal(prev => {
          return {
            ...prev,
            editIntegrator: true
          };
        });
      } else {
        redirectToEdit();
      }
    },
    posting_pictures: () => {
      sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Consejos-Descripcion');
      if (isIntegrator) {
        setShowModal(prev => {
          return {
            ...prev,
            editIntegrator: true
          };
        });
      } else {
        redirectToEdit();
      }
    },
    posting_quality: () => {
      sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Consejos-Completitud');
      openTipsModal();
    },
    posting_price: () => {
      sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Consejos-Precio');
      if (isIntegrator) {
        setShowModal(prev => {
          return {
            ...prev,
            editIntegrator: true
          };
        });
      } else {
        redirectToEdit();
      }
    },
    posting_exposure: () => {
      sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Consejos-Exposicion');
      isParticular ? window.open(`${window.location.origin}${linkUpgradePlan}`, '_blank') : openUpgradeModal();
    },
    posting_reports: () => {
      sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Consejos-Reportes');
      openReportModal();
    },
    posting_duplicated: () => {
      sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Consejos-Duplicados');
      setShowModal(prev => {
        return {
          ...prev,
          endModal: true
        };
      });
    },
    posting_penalized: () => {
      sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Consejos-Clasificados-Emprendimientos');
      setShowModal(prev => {
        return {
          ...prev,
          endModal: true
        };
      });
    },
    posting_rentedOrSold: () => {
      sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Consejos-Alquilados-Vendidos');
      setShowModal(prev => {
        return {
          ...prev,
          endModal: true
        };
      });
    }
  };

  const handleAfterChange = () => {
    sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Consejos_Movimiento');
  };

  const getTipWordingInfo = tip => {
    const { tipId } = tip;
    const wordingData = {
      posting_reports: { number: tip.tipsResponse.totalReport, isPlural: tip.tipsResponse.totalReport > 1 ? 's' : '' }
    };
    return wordingData[tipId] || {};
  };

  const getComponentDetail = (tipId, tipData) => {
    const componentsByTip = {
      posting_quality: !qualityNotApply && (
        <QualityCard
          quality={percentageCorrectness}
          qualityNotApply={qualityNotApply}
          device={device}
          qualityStatus={qualityStatus}
          qualityText={getQualityText()}
          isDuplicate={isDuplicate}
          isPenalized={isPenalized}
          isPostingWithoutQuality={isPostingWithoutQuality}
          messageWithoutQuality={messageWithoutQuality}
        />
      ),
      posting_price: <PriceCard priceData={tipData && tipData.tipsResponse} />
    };
    if (isPostingWithoutQuality) return componentsByTip.posting_quality;
    return componentsByTip[tipId] || null;
  };

  const getDisabledCTA = type => {
    const disabledCTAs = {
      posting_exposure: publicationPlan === 'SUPERHIGHLIGHTED' || isAssistant,
      posting_main_picture: isAssistant,
      posting_pictures: isAssistant,
      posting_price: isAssistant
    };
    return disabledCTAs[type] || false;
  };

  const getHiddenCTA = type => {
    const hiddenCTAs = {
      posting_price: isIntegrator
    };
    return hiddenCTAs[type] || false;
  };

  const getCaseWithoutQuality = () => {
    if (isDuplicate)
      return {
        tipId: 'posting_duplicated'
      };
    if (isPenalized)
      return {
        tipId: 'posting_penalized'
      };
    if (isRentedOrSold)
      return {
        tipId: 'posting_rentedOrSold'
      };
    return null;
  };

  return (
    <ContainerLayout device={device}>
      <CarouselHeader data-panel='carousel-header-title'>{t('panel.desempeno.tips.title')}</CarouselHeader>
      <CarouselSubtitle data-panel='carousel-header-subTitle'>{t('panel.desempeno.tips.subtitle')}</CarouselSubtitle>
      {isPostingWithoutQuality ? (
        <TipCard
          key={getCaseWithoutQuality().tipId}
          tipData={getCaseWithoutQuality()}
          device={device}
          ctaHandler={tipsActionHandlers[getCaseWithoutQuality().tipId]}
          componentDetail={getComponentDetail(getCaseWithoutQuality().tipId)}
          isPostingWithoutQuality={isPostingWithoutQuality}
        />
      ) : (
        <Carousel
          responsive={responsive}
          swipeable
          draggable
          showDots={device === 'desktop'}
          renderDotsOutside
          shouldResetAutoplay={false}
          customDot={<CustomDot />}
          minimumTouchDrag={120}
          partialVisible
          customButtonGroup={device === 'desktop' ? <ButtonGroup /> : null}
          arrows={false}
          renderButtonGroupOutside={true}
          slidesToSlide={1}
          afterChange={handleAfterChange}
        >
          {filteredTips &&
            filteredTips.map(tip => {
              return (
                <TipCard
                  key={tip.tipId}
                  tipData={tip}
                  device={device}
                  ctaHandler={tipsActionHandlers[tip.tipId]}
                  wordingInfo={getTipWordingInfo(tip)}
                  componentDetail={getComponentDetail(tip.tipId, tip)}
                  isDisabled={getDisabledCTA(tip.tipId)}
                  hiddenButton={getHiddenCTA(tip.tipId)}
                />
              );
            })}
        </Carousel>
      )}
    </ContainerLayout>
  );
};

const ContainerLayout = styled.div`
  flex-direction: column;
  padding-bottom: 40px;
  position: relative;
`;

const CustomDot = ({ onClick, ...rest }) => {
  const {
    index,
    active,
    carouselState: { totalItems }
  } = rest;

  const dotTemplate = id => <div className='dot-item' data={`desempeno-carousel-dot-${id}`} />;
  const carouselItems = [];
  const dotsNeeded = Math.round(totalItems / 2);

  let i = 0;
  while (i < dotsNeeded) {
    carouselItems.push(dotTemplate(i));
    i++;
  }

  return (
    <CarouselDotButton className={active ? 'active' : 'inactive'} active={active} onClick={onClick}>
      {React.Children.toArray(carouselItems)[index]}
    </CarouselDotButton>
  );
};

const CarouselDotButton = styled.button`
  .dot-item {
    border-radius: 50%;
    width: ${({ active }) => (active ? 8 : 6)}px;
    height: ${({ active }) => (active ? 8 : 6)}px;
    margin: 0 4px;
    cursor: pointer;
    background-color: ${({ active }) => (active ? getColor('primary') : 'black')};
  }
`;

const CarouselHeader = styled.div`
  font-family: 'Hind';
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  display: flex;
  align-items: center;
  > span {
    font-size: 10px;
    font-weight: 700;
  }
`;

const CarouselSubtitle = styled.div`
  margin-bottom: 24px;
  font-family: 'Hind';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  color: ${select('darkGray')};
  white-space: pre-wrap;
`;

const CarouselArrow = styled.button`
  position: absolute;
  width: 40px;
  height: 40px;
  border: 1px solid ${select('middleGray')};
  border-radius: 50%;
  top: 250px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ isDisabled }) => select(isDisabled ? 'lightGray' : 'white')};

  div {
    background-image: ${({ isDisabled }) => `url(${isDisabled ? ArrowDisabled : Arrow})`};
    height: 16px;
    width: 16px;
    background-position: center;
    background-repeat: no-repeat;
    ${({ direction, isDisabled }) => {
      if (direction === 'left' && !isDisabled) {
        return css`
          transform: rotate(180deg);
        `;
      } else if (direction !== 'left' && isDisabled) {
        return css`
          transform: rotate(180deg);
        `;
      }
    }}
  }

  ${({ direction }) =>
    direction === 'left'
      ? css`
          left: -20px;
        `
      : css`
          right: -20px;
        `}
`;

const CarouselButtonGroup = styled.div``;

const ButtonGroup = ({ next, previous, goToSlide, ...rest }) => {
  const {
    carouselState: { currentSlide, slidesToShow, totalItems }
  } = rest;
  return (
    <CarouselButtonGroup>
      <CarouselArrow
        direction='left'
        isDisabled={currentSlide === 0}
        onClick={() => currentSlide !== 0 && previous()}
        data-panel='desempeno-carousel-arrow-left'
      >
        <div />
      </CarouselArrow>
      <CarouselArrow
        isDisabled={currentSlide === totalItems - slidesToShow}
        onClick={() => currentSlide !== totalItems - slidesToShow && next()}
        data-panel='desempeno-carousel-arrow-right'
      >
        <div />
      </CarouselArrow>
    </CarouselButtonGroup>
  );
};

export default TipCarousel;
